import React, { useState } from "react";
import { InlineLoading } from "../../../../../../common/components/Loading";
import { LoadingButton } from "../../../../../../common/components/LoadingButton";
import { OrganizationDataRecipientDto } from "../../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { VendorDto } from "../../../../../../common/service/server/dto/VendorDto";
import { Callout, CalloutVariant } from "../../../../Callout";
import { DataRecipientLogo } from "../../../../DataRecipientLogo";
import { NestedVendorList } from "./NestedVendorList";

// Automation outcome values recorded on `data_subject_request_vendors`.
export const AUTOMATION_OUTCOMES = [
  "AUTOMATION_FAILED",
  "AUTOMATION_CONFIRMED",
  "AUTOMATION_REQUESTED",
  "AUTOMATION_NO_DATA",
  "AUTOMATION_WAITING",
];

export type AutomationOutcomeVariant = "delete" | "opt-out" | "access";

const CALLOUT_COPY: Record<
  AutomationOutcomeVariant,
  Record<string, { variant: CalloutVariant; title: string }>
> = {
  delete: {
    AUTOMATION_REQUESTED: {
      variant: CalloutVariant.Green,
      title: "Request to delete data automatically sent",
    },
    AUTOMATION_CONFIRMED: { variant: CalloutVariant.Green, title: "Data automatically deleted" },
    AUTOMATION_NO_DATA: {
      variant: CalloutVariant.Secondary,
      title: "Vendor indicated no data to delete for the consumer",
    },
    AUTOMATION_FAILED: {
      variant: CalloutVariant.Yellow,
      title: "Automatic deletion not successful",
    },
  },
  "opt-out": {
    AUTOMATION_REQUESTED: {
      variant: CalloutVariant.Green,
      title: "Request to remove automatically sent",
    },
    AUTOMATION_CONFIRMED: {
      variant: CalloutVariant.Green,
      title: "Consumer automatically removed",
    },
    AUTOMATION_NO_DATA: {
      variant: CalloutVariant.Secondary,
      title: "Vendor indicated no data to remove for the consumer",
    },
    AUTOMATION_FAILED: {
      variant: CalloutVariant.Yellow,
      title: "Automatic removal not successful",
    },
  },
  access: {
    AUTOMATION_REQUESTED: {
      variant: CalloutVariant.Green,
      title: "Access request automatically sent",
    },
    AUTOMATION_CONFIRMED: {
      variant: CalloutVariant.Green,
      title: "Access request automatically completed",
    },
    AUTOMATION_NO_DATA: {
      variant: CalloutVariant.Secondary,
      title: "Vendor indicated no data for the consumer",
    },
    AUTOMATION_FAILED: {
      variant: CalloutVariant.Yellow,
      title: "Automatic access request not successful",
    },
  },
};

/**
 * A read-only row summarizing an automation outcome for a vendor that is not otherwise shown as an
 * outcome row in the step (e.g. a vendor that has been filtered out of the vendor list but still has
 * a recorded automation outcome on the request).
 *
 * For the access variant, an in-flight handoff renders as a waiting row (spinner) until the vendor
 * responds, and a failed handoff renders a retry button when `onRetry` is provided.
 *
 * `nestedVendors` lists the recipients this vendor handles on the org's behalf (POL-7958); they are hidden from the
 * step's own vendor list and shown here instead.
 */
export const AutomationOutcomeRow: React.FC<{
  vendor: VendorDto;
  outcome: string;
  variant: AutomationOutcomeVariant;
  onRetry?: () => Promise<void>;
  nestedVendors?: OrganizationDataRecipientDto[];
  /** Recipients whose data is retrieved via this vendor on Access requests (DES-43). */
  retrievedViaVendors?: OrganizationDataRecipientDto[];
}> = ({ vendor, outcome, variant, onRetry, nestedVendors, retrievedViaVendors }) => {
  const isWaiting = outcome === "AUTOMATION_WAITING";
  const resolvedAutomatically = outcome !== "AUTOMATION_FAILED" && !isWaiting;
  const copy = CALLOUT_COPY[variant][outcome];
  const [isRetrying, setIsRetrying] = useState(false);

  // The nested list wraps onto its own line below the name and callout, so it never stretches the line they are
  // centered on, however many lines the vendor name wraps to (POL-7992).
  return (
    <div className="flex-container flex--wrap flex--align-center pt-std pb-std border-top border-top--grey">
      <div className="text-t3 w-256 pl-xs mr-mdlg">
        <div className="flex-container flex--align-center">
          <DataRecipientLogo dataRecipient={vendor} className="align-top mr-mdlg" />
          <span>{vendor.name}</span>
          {resolvedAutomatically && (
            <img
              src="/assets/images/automation-icon.svg"
              alt="Resolved automatically"
              className="ml-xs"
            />
          )}
        </div>
      </div>
      <div className="flex--expand-self">
        {isWaiting ? (
          <Callout
            variant={CalloutVariant.Secondary}
            title={
              <span className="flex-container flex--align-center">
                <InlineLoading size="sm" />
                <span className="ml-sm">Waiting for {vendor.name} response…</span>
              </span>
            }
          />
        ) : (
          copy && (
            <Callout
              variant={copy.variant}
              title={copy.title}
              subtitle={
                outcome === "AUTOMATION_FAILED" && onRetry ? (
                  <div className="mt-sm">
                    <LoadingButton
                      variant="contained"
                      className="manually-automate-request-button"
                      loading={isRetrying}
                      onClick={async () => {
                        setIsRetrying(true);
                        try {
                          await onRetry();
                        } finally {
                          setIsRetrying(false);
                        }
                      }}
                    >
                      <img src="/assets/images/automation-icon.svg" alt="" /> Retry Now
                    </LoadingButton>
                  </div>
                ) : undefined
              }
            />
          )
        )}
      </div>
      <div className="w-100">
        <div className="w-256">
          <NestedVendorList managerName={vendor.name} nestedVendors={nestedVendors} />
          <NestedVendorList
            managerName={vendor.name}
            nestedVendors={retrievedViaVendors}
            title="Also includes data from:"
          />
        </div>
      </div>
    </div>
  );
};
